/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ClipboardIcon from '@atlaskit/icon/core/clipboard';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const collapseMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardWrapper.collapse',
	defaultMessage: 'Collapse',
	description:
		'Button label or action text displayed on a button or clickable element in an action card header within the AI conversation interface. Shown as clickable text on a collapse/expand control. When clicked, it collapses the action card to hide its content and show only the header. The purpose is to allow users to minimize action cards to save screen space and reduce visual clutter in the conversation view.',
});

const numberLabelMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardWrapper.numberLabel.non-final',
	defaultMessage: '{index}.',
	description: 'Ordinal number shown before a sub-plan title in an action card, e.g. "1.".',
});

const expandMessage = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.ActionCardWrapper.expand',
	defaultMessage: 'Expand',
	description:
		'Button label or action text displayed on a button or clickable element in a collapsed action card header within the AI conversation interface. Shown as clickable text on a collapse/expand control. When clicked, it expands the action card to reveal its full content. The purpose is to allow users to view the complete action card details when it has been previously collapsed.',
});

const styles = cssMap({
	actionCardWrapper: {
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
	},
	actionCardHeader: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
	},
	actionCardHeaderPaddingBottom: {
		paddingBottom: token('space.150'),
	},
	topRadius: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},
	bottomRadius: {
		borderBottomLeftRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
	},
	noBorderTopOrBottom: {
		borderTopStyle: 'none',
		borderBottomStyle: 'none',
	},
	radiusFull: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
	},
	borderTop: {
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
	},
	actionCardContentWrapper: {
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
	},
	titleBold: {
		font: token('font.heading.small'),
	},
});

export const ActionCardWrapper = ({
	title,
	subtitle,
	children,
	isSubPlan = false,
	defaultExpanded = false,
	borderOverride,
	index,
	isExpandable,
}: {
	title?: string;
	subtitle?: React.ReactNode;
	children: React.ReactNode;
	isSubPlan?: boolean;
	defaultExpanded?: boolean;
	borderOverride?: 'top' | 'bottom' | 'both';
	/** When provided, an ordinal (e.g. "1.") is shown before the title - used to number sub-plans. */
	index?: number;
	isExpandable: boolean;
}): JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={cx(
				styles.actionCardWrapper,
				(!isSubPlan || borderOverride === 'both') && styles.radiusFull,
				isSubPlan && borderOverride === 'top' && styles.topRadius,
				isSubPlan && borderOverride === 'bottom' && styles.bottomRadius,
				isSubPlan && !borderOverride && styles.noBorderTopOrBottom,
			)}
			testId="action-card-wrapper"
		>
			<Box
				xcss={cx(
					styles.actionCardHeader,
					(isSubPlan || !isExpanded) && styles.actionCardHeaderPaddingBottom,
				)}
			>
				<Inline space="space.100" alignBlock="center" spread="space-between">
					<Inline space="space.100" alignBlock="center">
						{!isSubPlan && <ClipboardIcon label="Plan" />}
						<Stack space="space.025">
							<Inline space="space.050" alignBlock="center">
								{index !== undefined ? (
									<Box as="span" xcss={styles.titleBold}>
										{formatMessage(numberLabelMessage, { index })}
									</Box>
								) : null}
								<Box as="span" xcss={styles.titleBold}>
									{title}
								</Box>
							</Inline>
							<Text size="small" color="color.text.subtlest">
								{subtitle}
							</Text>
						</Stack>
					</Inline>
					{isExpandable && (
						<Inline space="space.050" alignBlock="center">
							<IconButton
								appearance="subtle"
								icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
								label={isExpanded ? formatMessage(collapseMessage) : formatMessage(expandMessage)}
								onClick={() => setIsExpanded(!isExpanded)}
							/>
						</Inline>
					)}
				</Inline>
			</Box>
			{isExpanded && (
				<Box xcss={cx(styles.actionCardContentWrapper, isSubPlan && styles.borderTop)}>
					{children}
				</Box>
			)}
		</Box>
	);
};
